// GAVION company-create: the company field of the contact / deal form (AutocompleteCompanyInput).
// Supabase: public.find_or_create_company / public.search_companies (supabase/schemas/69_gavion_company_create.sql);
// FakeRest (demo build, tests): the same rules in memory.
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  DataProvider,
  GetListParams,
  GetListResult,
  Identifier,
  RaRecord,
} from "ra-core";

/** The autocomplete's filter key (its filterToQuery): routes getList("companies") to the name search. */
export const COMPANY_SEARCH_FILTER = "company_search";

/** search_companies reads at most this many characters of the text. */
export const COMPANY_SEARCH_MAX_LENGTH = 200;

export type CompanyCandidate = {
  id: Identifier;
  name: string;
  city: string | null;
  region: string | null;
};

/** One company (reused or created), or several with the same name for the rep to choose from (none picked). */
export type FindOrCreateResult =
  | (CompanyCandidate & { created: boolean; candidates?: undefined })
  | { created: false; candidates: CompanyCandidate[] };

/** A refusal from find_or_create_company: `code` = the RPC's message (invalid_name, forbidden, …). */
export class CompanyCreateError extends Error {
  constructor(public readonly code: string) {
    super(code);
  }
}

export type CompanyCreateMethods = {
  /**
   * The company with the same name (case, accents and spacing ignored); several = the candidates, none picked;
   * none (or `force`, « Créer quand même ») = a new one owned by the caller.
   */
  findOrCreateCompany(
    name: string,
    force?: boolean,
  ): Promise<FindOrCreateResult>;
  /** Ids of the companies matching every word of `q` (name, city, phone, website…), best name match first (no count). */
  searchCompanyIds(q: string, limit: number): Promise<Identifier[]>;
};

/** « Name — City » (or the region): tells the branches of a franchise apart. */
export const companyLabel = (c: {
  name?: string | null;
  city?: string | null;
  region?: string | null;
}) => {
  const place = c.city || c.region;
  return place ? `${c.name ?? ""} — ${place}` : (c.name ?? "");
};

/** Mirror of public.search_unaccent + lower (PG unaccent also folds the ligatures). */
const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/œ/gi, "oe")
    .replace(/æ/gi, "ae")
    .toLowerCase();

/** The SQL set: [[:space:]] + U+00A0, U+1680, U+2000–U+200B, U+2028, U+2029, U+202F, U+205F, U+3000, U+FEFF. */
const SPACES =
  /[\s\u00a0\u1680\u2000-\u200b\u2028\u2029\u202f\u205f\u3000\ufeff]+/g;

/** Trimmed, inner whitespace collapsed (what find_or_create_company stores). */
export const cleanCompanyName = (name: string) =>
  name.replace(SPACES, " ").trim();

/** Mirror of public.company_name_key: the duplicate key. */
export const companyNameKey = (name: string) => fold(cleanCompanyName(name));

/** Mirror of public.search_query's words: unaccented, lowercased, split on anything not a letter or a digit. */
export const companySearchWords = (q: string) =>
  fold(q)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/** Every word of `q` is a prefix of a word of the text (search_document(…) @@ search_query(q)). */
const wordsMatch = (text: string, q: string) => {
  const words = companySearchWords(text);
  return companySearchWords(q).every((w) => words.some((n) => n.startsWith(w)));
};

/** Name only. */
export const companyMatches = (name: string, q: string) => wordsMatch(name, q);

type SearchRow = {
  id: Identifier;
  name?: string | null;
  sector?: string | null;
  description?: string | null;
  website?: string | null;
  phone_number?: string | null;
  zipcode?: string | null;
  city?: string | null;
  state_abbr?: string | null;
  region?: string | null;
  is_test?: boolean | null;
};

/** Mirror of companies.fts (08_): name, sector, description, website, phone, zip, city, state. */
const ftsText = (r: SearchRow) =>
  [
    r.name,
    r.sector,
    r.description,
    r.website,
    r.phone_number,
    r.zipcode,
    r.city,
    r.state_abbr,
  ]
    .filter(Boolean)
    .join(" ");

/** search_companies's order: exact name, name prefix, the name alone matches, then by name, then by id. */
export const rankCompanies = <T extends SearchRow>(
  rows: readonly T[],
  q: string,
): T[] => {
  const key = companyNameKey(q);
  const score = (r: T) => {
    const k = companyNameKey(r.name ?? "");
    return k === key
      ? 0
      : k.startsWith(key)
        ? 1
        : companyMatches(r.name ?? "", q)
          ? 2
          : 3;
  };
  const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  return [...rows].sort(
    (a, b) =>
      score(a) - score(b) ||
      cmp(companyNameKey(a.name ?? ""), companyNameKey(b.name ?? "")) ||
      Number(a.id) - Number(b.id),
  );
};

/**
 * PostgREST reads `,` `(` `)` as its or=(…) syntax and `"` `\` as quoting: in a search text they become spaces
 * (word separators for the ilike search), so « Ferblanterie Ouellet (Qc) inc » no longer breaks the request.
 */
export const cleanPostgrestSearch = (q: string) =>
  q
    .replace(/[,()"\\]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * getList("companies", {filter: {company_search}}) in both providers: the ids from the search, then those rows
 * (companies_summary) in the search order; ids the row read no longer returns are dropped; no ids = no row read.
 */
export const listCompaniesBySearch = async (
  searchCompanyIds: CompanyCreateMethods["searchCompanyIds"],
  getRows: (ids: Identifier[]) => Promise<RaRecord[]>,
  params: Pick<GetListParams, "filter" | "pagination">,
): Promise<GetListResult> => {
  const ids = await searchCompanyIds(
    String(params.filter?.[COMPANY_SEARCH_FILTER] ?? ""),
    params.pagination?.perPage ?? 10,
  );
  if (ids.length === 0) return { data: [], total: 0 };
  const byId = new Map(
    (await getRows(ids)).map((row) => [String(row.id), row]),
  );
  const data = ids.flatMap((id) => byId.get(String(id)) ?? []);
  return { data, total: data.length };
};

export const isCompanySearch = (params: { filter?: unknown }) =>
  !!params.filter &&
  typeof params.filter === "object" &&
  COMPANY_SEARCH_FILTER in params.filter;

const toCompanyCreateError = (e: { message: string }) =>
  new CompanyCreateError(e.message);

export const createSupabaseCompanyCreateMethods = (
  getClient: () => SupabaseClient,
): CompanyCreateMethods => ({
  async findOrCreateCompany(name, force = false) {
    const { data, error } = await getClient().rpc("find_or_create_company", {
      p_name: name,
      p_force: force,
    });
    if (error) throw toCompanyCreateError(error);
    return data as FindOrCreateResult;
  },
  async searchCompanyIds(q, limit) {
    const { data, error } = await getClient().rpc("search_companies", {
      p_q: q,
      p_limit: limit,
    });
    if (error) throw toCompanyCreateError(error);
    return (data ?? []) as Identifier[];
  },
});

const candidateOf = (c: SearchRow): CompanyCandidate => ({
  id: c.id,
  name: c.name ?? "",
  city: c.city ?? null,
  region: c.region ?? null,
});

/**
 * FakeRest twin. `getCreator` is the full data provider (lifecycle defaults apply); calls are serialised like the
 * advisory lock, so two quick calls with the same name return the same company.
 */
export const createFakeCompanyCreateMethods = (
  dp: DataProvider,
  currentSalesId: () => Promise<Identifier | null>,
  getCreator: () => DataProvider,
  isAdmin: () => Promise<boolean> = async () => false,
): CompanyCreateMethods => {
  let queue: Promise<unknown> = Promise.resolve();
  const all = async () =>
    (
      await dp.getList<SearchRow>("companies", {
        filter: {},
        pagination: { page: 1, perPage: 100_000 },
        sort: { field: "id", order: "ASC" },
      })
    ).data;
  return {
    findOrCreateCompany(name, force = false) {
      const run = async (): Promise<FindOrCreateResult> => {
        const clean = cleanCompanyName(name ?? "");
        if (!clean || clean.length > 300)
          throw new CompanyCreateError("invalid_name");
        if (!force) {
          const key = companyNameKey(clean);
          const admin = await isAdmin();
          const same = (await all())
            .filter(
              (c) =>
                companyNameKey(c.name ?? "") === key && (admin || !c.is_test),
            )
            .sort((a, b) => Number(a.id) - Number(b.id))
            .slice(0, 20)
            .map(candidateOf);
          if (same.length === 1) return { ...same[0], created: false };
          if (same.length > 1) return { created: false, candidates: same };
        }
        const { data } = await getCreator().create<SearchRow>("companies", {
          data: { name: clean, sales_id: await currentSalesId() },
        });
        return {
          id: data.id,
          name: data.name ?? clean,
          city: null,
          region: null,
          created: true,
        };
      };
      const result = queue.then(run, run);
      queue = result.catch(() => undefined);
      return result;
    },
    async searchCompanyIds(q, limit) {
      const max = Math.min(Math.max(limit || 10, 1), 50);
      const text = q.slice(0, COMPANY_SEARCH_MAX_LENGTH);
      const rows = await all();
      if (companySearchWords(text).length === 0)
        return rows
          .map((r) => r.id)
          .sort((a, b) => Number(b) - Number(a))
          .slice(0, max);
      return rankCompanies(
        rows.filter((r) => wordsMatch(ftsText(r), text)),
        text,
      )
        .slice(0, max)
        .map((r) => r.id);
    },
  };
};
